import React, { useEffect, useMemo, useState } from 'react';
import ImageLightbox from '../ImageLightbox';

// Shared product gallery: square hero + thumbnail grid + fullscreen lightbox.
// Used by BOTH product pages (factory catalog and supplier storefront) so the
// two read as one product experience.
//
// Broken-image handling is the reason this is a component rather than a snippet.
// `products.image_url` / `gallery_images` are not guaranteed to hold absolute
// URLs — catalogImport writes a field named `image_path`, the admin console
// guards it with isUrl(), and both branches of catalog_products_public filter
// on `^https?://`. Nothing filtered `gallery_images` anywhere, so a bare storage
// path could reach <img src> and resolve against the page URL. Two defences:
//   1. the caller passes images already filtered (getProductGalleryImages);
//   2. anything that still fails to load is dropped here at runtime, so a dead
//      URL degrades to the next image — or the initial tile — never a broken icon.
// `onLightboxChange` matters: the factory page walks product-to-product with the
// arrow keys and must stand down while the fullscreen viewer owns them. That
// guard used to read the page's own `lightbox` state — moving the state in here
// without reporting it back would have broken the guard silently.
export default function ProductGallery({ images = [], alt = '', zoomTitle, onLightboxChange }) {
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [failed, setFailed] = useState(() => new Set());

  const openLightbox = (open) => { setLightbox(open); onLightboxChange?.(open); };

  const all = useMemo(
    () => (Array.isArray(images) ? images : []).filter(Boolean),
    [images],
  );
  // Images that have actually loaded (or not been tried yet). A URL only leaves
  // this list once the browser has told us it is dead.
  const gallery = useMemo(() => all.filter((u) => !failed.has(u)), [all, failed]);

  // A new product resets the selection; keeping the old one would show the
  // previous product's photo until the next click.
  useEffect(() => { setSelected(null); setFailed(new Set()); }, [images]);

  const markFailed = (url) => setFailed((prev) => {
    if (prev.has(url)) return prev;
    const next = new Set(prev);
    next.add(url);
    return next;
  });

  const mainImg = (selected && !failed.has(selected) ? selected : null) || gallery[0] || null;
  const initial = (alt || '?')[0];

  return (
    <>
      <button
        type="button"
        onClick={() => mainImg && openLightbox(true)}
        title={mainImg ? zoomTitle : undefined}
        style={{
          width: '100%', aspectRatio: '1 / 1', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)', overflow: 'hidden', background: 'var(--bg-hero)',
          padding: 0, cursor: mainImg ? 'zoom-in' : 'default', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
        {mainImg
          ? <img src={mainImg} alt={alt} onError={() => markFailed(mainImg)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 72, fontWeight: 300, color: '#8B7355' }}>{initial}</span>}
      </button>

      {gallery.length > 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8, marginTop: 10 }}>
          {gallery.map((img, i) => (
            <button key={`${img}-${i}`} type="button" onClick={() => setSelected(img)}
              style={{
                height: 68, padding: 0, borderRadius: 'var(--radius-control)', overflow: 'hidden', cursor: 'pointer',
                background: 'var(--bg-hero)',
                border: `1px solid ${mainImg === img ? 'var(--border-strong)' : 'var(--border)'}`,
              }}>
              <img src={img} alt="" onError={() => markFailed(img)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}

      {lightbox && gallery.length > 0 && (
        <ImageLightbox
          images={gallery}
          start={Math.max(0, gallery.indexOf(mainImg))}
          alt={alt}
          onClose={() => openLightbox(false)}
        />
      )}
    </>
  );
}
